import { CheckIcon, PlusIcon } from '@heroicons/react/24/outline';
import React from 'react';
import { DraftNote, DraftPerson, DraftTask, DUE_LABELS, DueChoice } from './wrapup.helpers';

// #region Check
/** A big, soft, tappable check — the whole row toggles, not a tiny box. */
const Check: React.FunctionComponent<{ on: boolean }> = ({ on }) => (
    <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-all duration-200 ${on ? 'scale-100 border-emerald-500 bg-emerald-500 text-white' : 'border-zinc-300 bg-transparent text-transparent dark:border-zinc-600'}`}>
        <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} />
    </span>
);

// #region AddRow
const AddRow: React.FunctionComponent<{ placeholder: string; onAdd: (text: string) => void }> = ({ placeholder, onAdd }) => {
    const [text, setText] = React.useState<string>('');
    const commit = () => { const t = text.trim(); if (!t) return; onAdd(t); setText(''); };
    return (
        <div className="flex items-center gap-2 pl-1">
            <PlusIcon className="h-4 w-4 text-zinc-400" />
            <input
                value={text} placeholder={placeholder} onChange={(ev) => setText(ev.target.value)}
                onKeyDown={(ev) => { if (ev.key === 'Enter') { ev.preventDefault(); commit(); } }} onBlur={commit}
                className="w-full bg-transparent py-1.5 text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none dark:text-zinc-100"
            />
        </div>
    );
};

// #region NotesSection
export interface NotesSectionProps { notes: DraftNote[]; onChange: (notes: DraftNote[]) => void }

export const NotesSection: React.FunctionComponent<NotesSectionProps> = ({ notes, onChange }) => (
    <ul className="flex flex-col gap-y-1">
        {notes.map((n) => (
            <li key={n.key}>
                <button type="button" onClick={() => onChange(notes.map((x) => x.key === n.key ? { ...x, on: !x.on } : x))}
                    className={`flex w-full items-start gap-3 rounded-xl px-2 py-2 text-left transition hover:bg-zinc-500/5 ${n.on ? '' : 'opacity-45'}`}>
                    <Check on={n.on} />
                    <span className={`text-sm leading-6 text-zinc-800 dark:text-zinc-200 ${n.on ? '' : 'line-through'}`}>{n.text}</span>
                </button>
            </li>
        ))}
        <li><AddRow placeholder="Add a note…" onAdd={(text) => onChange([...notes, { key: `n${Date.now()}`, text, on: true }])} /></li>
    </ul>
);

// #region TasksSection
export interface TasksSectionProps { tasks: DraftTask[]; onChange: (tasks: DraftTask[]) => void }

const DUE_ORDER: DueChoice[] = ['tomorrow', 'three_days', 'next_week', 'none'];

export const TasksSection: React.FunctionComponent<TasksSectionProps> = ({ tasks, onChange }) => {
    const patch = (key: string, p: Partial<DraftTask>) => onChange(tasks.map((t) => t.key === key ? { ...t, ...p } : t));
    return (
        <ul className="flex flex-col gap-y-1">
            {tasks.map((t) => (
                <li key={t.key} className={`rounded-xl px-2 py-2 transition hover:bg-zinc-500/5 ${t.on ? '' : 'opacity-45'}`}>
                    <div className="flex items-start gap-3">
                        <button type="button" onClick={() => patch(t.key, { on: !t.on })} aria-label={t.on ? 'Drop this task' : 'Keep this task'}><Check on={t.on} /></button>
                        <div className="min-w-0 flex-1">
                            <input
                                value={t.text} onChange={(ev) => patch(t.key, { text: ev.target.value })}
                                className={`w-full bg-transparent text-sm leading-6 text-zinc-900 focus:outline-none dark:text-zinc-100 ${t.on ? '' : 'line-through'}`}
                            />
                            <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                {t.owner === 'prospect' ? <span className="rounded-full bg-sky-500/10 px-2 py-0.5 text-[11px] font-medium text-sky-700 dark:text-sky-300">Theirs</span> : null}
                                {DUE_ORDER.map((d) => (
                                    <button key={d} type="button" onClick={() => patch(t.key, { due: d, on: true })}
                                        className={`rounded-full px-2 py-0.5 text-[11px] font-medium transition ${t.due === d ? 'bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900' : 'bg-zinc-500/10 text-zinc-600 hover:bg-zinc-500/20 dark:text-zinc-300'}`}>
                                        {DUE_LABELS[d]}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </li>
            ))}
            <li><AddRow placeholder="Add a task for yourself…" onAdd={(text) => onChange([...tasks, { key: `a${Date.now()}`, text, owner: 'rep', due: 'tomorrow', on: true }])} /></li>
        </ul>
    );
};

// #region OutcomeToggle
export interface OutcomeToggleProps { value: 'completed' | 'no_show' | null; onChange: (v: 'completed' | 'no_show') => void }

/** Apple-style segmented control: held or missed. */
export const OutcomeToggle: React.FunctionComponent<OutcomeToggleProps> = ({ value, onChange }) => (
    <div className="inline-flex rounded-full bg-zinc-500/10 p-1">
        {([['completed', 'It happened'], ['no_show', 'No-show']] as const).map(([v, label]) => (
            <button key={v} type="button" onClick={() => onChange(v)}
                className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-all duration-200 ${value === v ? 'bg-white text-zinc-900 shadow-sm dark:bg-zinc-800 dark:text-zinc-100' : 'text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200'}`}>
                {label}
            </button>
        ))}
    </div>
);

// #region PersonSection
export interface PersonSectionProps { person: DraftPerson; onChange: (p: DraftPerson) => void }

const fieldCls = 'w-full rounded-xl border border-transparent bg-zinc-500/5 px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 transition focus:border-emerald-500/40 focus:bg-transparent focus:outline-none dark:text-zinc-100';

/** The CRM has no lead for this call yet: name and email make one (or match one), company is a bonus. */
export const PersonSection: React.FunctionComponent<PersonSectionProps> = ({ person, onChange }) => (
    <div className="flex flex-col gap-y-2">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <input value={person.name} placeholder="Their name" onChange={(ev) => onChange({ ...person, name: ev.target.value })} className={fieldCls} />
            <input value={person.email} type="email" placeholder="Their email" onChange={(ev) => onChange({ ...person, email: ev.target.value })} className={fieldCls} />
        </div>
        <input value={person.company} placeholder="Company (optional)" onChange={(ev) => onChange({ ...person, company: ev.target.value })} className={fieldCls} />
        <p className="text-xs text-zinc-400">Wrapping up attaches everything to this person&apos;s lead, creating it if they&apos;re new.</p>
    </div>
);
